import {
  BucketName,
  BusinessMerchantServiceChargeFee,
} from "../../../../../interfaces/businessFull.interface";
import {
  IBucketGroups,
  IOnlinePurchPriceProfile,
  IPurchPriceProfile,
  InPersonBucketGroupCoverageEntry,
  OnlinePartnerBucketGroups,
} from "../../../../../interfaces/partner.interface";
import {
  BusinessOnlinePricing,
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../../../interfaces/onlinePricing.interface";
import { detectOnlinePartnerPricingStructure } from "../../../../partners/constants/onlineBuckets";
import type { UpdateBusinessPricingPayload } from "../../../businessesApi";
import { storedPartnerId } from "../../../utils/isNoEdit";

export type ValueType = "Fixed" | "Percentage";

export type FeeStatus =
  | { kind: "markup"; text: string }
  | { kind: "above" }
  | { kind: "below" }
  | null;

export type RangeGroup = {
  key: string;
  label: string;
  floor: number;
  max: number;
  valueType: ValueType;
  // `comparable` is false when the fee and its group are in different units —
  // a Percentage fee has no position inside a Fixed euro range.
  fees: {
    index: number;
    fee: BusinessMerchantServiceChargeFee;
    comparable: boolean;
  }[];
};

// Stored fee name → mock display label (only "European Debit" is relabelled).
export const FEE_DISPLAY_LABEL: Record<string, string> = {
  "European Debit": "E.E.A Debit",
};
export const displayFeeLabel = (name: string): string =>
  FEE_DISPLAY_LABEL[name] ?? name;

export function selectPricingProfile<T extends { _id: string; name: string }>(
  profiles: T[] | undefined,
  profileId: string | null | undefined
): T | undefined {
  if (profileId) {
    return profiles?.find((profile) => profile._id === profileId);
  }
  return (
    profiles?.find((profile) => profile.name === "default") ?? profiles?.[0]
  );
}

/** Display order for the in-person buckets. The online pane lists the same card
 *  buckets in this order, and a merchant's stored array is in whatever order it
 *  was written in. */
export const IN_PERSON_BUCKET_ORDER: BucketName[] = [
  BucketName.DomesticDebit,
  BucketName.DomesticCredit,
  BucketName.DomesticCorporateDebit,
  BucketName.DomesticCorporateCredit,
  BucketName.EuropeanDebit,
  BucketName.EuropeanCredit,
  BucketName.EuropeanCorporateCredit,
  BucketName.EuropeanCorporateDebit,
  BucketName.InternationalDebit,
  BucketName.InternationalCredit,
  BucketName.InternationalCorporateDebit,
  BucketName.InternationalCorporateCredit,
  BucketName.InPersonAmex,
  BucketName.ForeignCreditCards,
  BucketName.ForeignDebitCards,
];

/** The card buckets every priced direct merchant stores, Amex aside. The two
 *  Foreign buckets are legacy and are not offered to a merchant priced anew. */
export const DIRECT_IN_PERSON_CARD_BUCKETS: BucketName[] =
  IN_PERSON_BUCKET_ORDER.filter(
    (name) =>
      name !== BucketName.InPersonAmex &&
      name !== BucketName.ForeignCreditCards &&
      name !== BucketName.ForeignDebitCards
  );

/** A merchant with no partner whose stored in-person list is empty has no row
 *  to edit, so the card offers the standard buckets unpriced (PC-1394). Only a
 *  stored `[]` counts: an absent list means pricing was not loaded, not that
 *  the merchant has none. A partner merchant takes its rows from the range. */
export function needsInPersonPricing(
  business:
    | {
        partnerId?: unknown;
        merchantServiceChargeFees?: BusinessMerchantServiceChargeFee[];
      }
    | null
    | undefined
): boolean {
  const stored = business?.merchantServiceChargeFees;
  return (
    !storedPartnerId(business?.partnerId) &&
    Array.isArray(stored) &&
    stored.length === 0
  );
}

/** The standard card buckets with no rate yet. The value is a placeholder: the
 *  card renders these rows empty and refuses to save until each has a rate. */
export function unpricedInPersonCardFees(): BusinessMerchantServiceChargeFee[] {
  return DIRECT_IN_PERSON_CARD_BUCKETS.map((name) => ({
    name,
    value: 0,
    valueType: "Percentage" as const,
  }));
}

/** Which rows to render, in display order, as indices into `fees`. Indices
 *  rather than fees: the same array is the save payload and is edited by index,
 *  so only the rendering may be reordered. */
export function inPersonFeeOrder(
  fees: BusinessMerchantServiceChargeFee[]
): number[] {
  const indices = fees.map((_, index) => index);
  return [
    ...IN_PERSON_BUCKET_ORDER.flatMap((name) =>
      indices.filter((index) => fees[index].name === name)
    ),
    ...indices.filter(
      (index) =>
        !IN_PERSON_BUCKET_ORDER.includes(fees[index].name as BucketName)
    ),
  ];
}

export const GROUP_ORDER: string[] = [
  "domesticDebitCards",
  "domesticCreditCards",
  "europeanDebitCards",
  "european",
  "internationalAndCommercial",
  "amex",
];
export const GROUP_LABEL: Record<string, string> = {
  domesticDebitCards: "Domestic Debit",
  domesticCreditCards: "Domestic Credit",
  europeanDebitCards: "E.E.A Debit",
  european: "E.E.A",
  internationalAndCommercial: "Non E.E.A & Corporate",
  amex: "AMEX",
};

export const fmt = (value: number, t: ValueType): string =>
  `${parseFloat(value.toFixed(4))}${t === "Fixed" ? "€" : "%"}`;

/** The coverage group a fee name belongs to. `null` is an explicit ungrouped
 *  name (foreign cards); `undefined` means the read did not place it. */
export function inPersonGroupKeyForFee(
  feeName: string,
  coverage?: InPersonBucketGroupCoverageEntry[] | null
): string | null | undefined {
  if (!coverage?.length) return undefined;
  const entry = coverage.find((row) => row.feeNames?.includes(feeName));
  return entry ? entry.group : undefined;
}

export const hasInPersonGroupCoverage = (
  profile?: Pick<IPurchPriceProfile, "inPersonBucketGroupCoverage"> | null
): boolean => !!profile?.inPersonBucketGroupCoverage?.length;

/** A profile that prices at least one in-person group, even if coverage was
 *  omitted from the read that loaded it. */
export const hasPricedInPersonBucketGroups = (
  profile?: { bucketGroups?: Partial<IBucketGroups> | null } | null
): boolean =>
  !!profile?.bucketGroups &&
  Object.values(profile.bucketGroups).some(
    (group) => group && typeof group.value === "number"
  );

export function isSixteenBucketOnlineProfile(
  profile: IOnlinePurchPriceProfile | undefined
): boolean {
  if (!profile) return false;
  if (profile.onlinePricingStructure === OnlinePricingStructure.SixteenBucket) {
    return true;
  }
  if (profile.onlinePricingStructure === OnlinePricingStructure.FourBucket) {
    return false;
  }
  // Card-split exists only on sixteen-bucket ranges.
  if (profile.onlineCardSplitTemplate) return true;
  return (
    detectOnlinePartnerPricingStructure(
      profile.bucketGroups as OnlinePartnerBucketGroups | undefined,
      profile.defaultOnlinePricing as OnlinePartnerBucketGroups | undefined
    ) === OnlinePricingStructure.SixteenBucket
  );
}

export function selectOnlinePricingProfile(
  profiles: IOnlinePurchPriceProfile[] | undefined,
  profileId: string | null | undefined,
  preferSixteenBucket = false
): IOnlinePurchPriceProfile | undefined {
  if (profileId) {
    return profiles?.find((profile) => profile._id === profileId);
  }
  if (preferSixteenBucket) {
    const sixteen = profiles?.filter(isSixteenBucketOnlineProfile);
    if (sixteen?.length) return selectPricingProfile(sixteen, undefined);
  }
  return selectPricingProfile(profiles, undefined);
}

/** One merchant fee per coverage name, pre-filled at the group's floor. Ungrouped
 *  names (foreign cards) are skipped — they have no group to take a rate from. */
export function merchantFeesFromInPersonProfile(
  profile: IPurchPriceProfile | undefined
): BusinessMerchantServiceChargeFee[] {
  if (!profile) return [];
  const coverage = profile.inPersonBucketGroupCoverage;
  if (!coverage?.length) return [];
  const fees: BusinessMerchantServiceChargeFee[] = [];
  coverage.forEach((entry) => {
    if (!entry.group || !entry.feeNames?.length) return;
    const group = profile.bucketGroups?.[entry.group as keyof IBucketGroups];
    if (!group || typeof group.value !== "number") return;
    entry.feeNames.forEach((name) => {
      fees.push({
        name,
        value: group.value,
        valueType: group.valueType,
      });
    });
  });
  return fees;
}

// A fee is in range when floor <= value <= max; markup is value - floor.
export function feeStatus(
  value: number,
  valueType: ValueType,
  floor: number,
  max: number
): FeeStatus {
  if (value < floor) return { kind: "below" };
  if (value > max) return { kind: "above" };
  return { kind: "markup", text: `Markup +${fmt(value - floor, valueType)}` };
}

// Group in-person fees under their partner range group, preserving GROUP_ORDER.
export function buildRangeGroups(
  fees: BusinessMerchantServiceChargeFee[],
  profile: IPurchPriceProfile | undefined
): RangeGroup[] {
  if (!profile || !hasInPersonGroupCoverage(profile)) return [];
  const byKey: Record<string, RangeGroup> = {};
  fees.forEach((fee, index) => {
    const groupKey = inPersonGroupKeyForFee(
      fee.name,
      profile.inPersonBucketGroupCoverage
    );
    if (!groupKey) return;
    const group = profile.bucketGroups[groupKey as keyof IBucketGroups];
    if (!group || group.value === undefined) return;
    if (!byKey[groupKey]) {
      byKey[groupKey] = {
        key: groupKey,
        label: GROUP_LABEL[groupKey] ?? groupKey,
        floor: group.value,
        max: group.max,
        valueType: group.valueType,
        fees: [],
      };
    }
    byKey[groupKey].fees.push({
      index,
      fee,
      comparable: fee.valueType === group.valueType,
    });
  });
  return GROUP_ORDER.filter((k) => byKey[k]).map((k) => byKey[k]);
}

// Fees whose name has no mapping under the profile's coverage — surfaced so a
// value the KBM user can't see is never silently saved. Missing coverage lists
// every fee; `group: null` names stay here too.
export function unmappedFees(
  fees: BusinessMerchantServiceChargeFee[],
  profile: IPurchPriceProfile | undefined
): { index: number; fee: BusinessMerchantServiceChargeFee }[] {
  if (!profile) return [];
  if (!hasInPersonGroupCoverage(profile)) {
    return fees.map((fee, index) => ({ index, fee }));
  }
  return fees
    .map((fee, index) => ({ index, fee }))
    .filter(({ fee }) => {
      const groupKey = inPersonGroupKeyForFee(
        fee.name,
        profile.inPersonBucketGroupCoverage
      );
      if (!groupKey) return true;
      const group = profile.bucketGroups[groupKey as keyof IBucketGroups];
      return !group || group.value === undefined;
    });
}

// A fee in a different unit from its group is left out: it can be neither above
// nor below a range it isn't measured in, so it must not gate the save.
export function inPersonHasOutOfRange(groups: RangeGroup[]): boolean {
  return groups.some((g) =>
    g.fees.some(({ fee, comparable }) => {
      if (!comparable) return false;
      const s = feeStatus(fee.value, g.valueType, g.floor, g.max);
      return s?.kind === "above" || s?.kind === "below";
    })
  );
}

/** Whether a save that first maps the merchant without carrying in-person fees
 *  would be refused: the endpoint then bands the STORED fees, in full, against
 *  the stored profile id's range or the partner's `default` one. Zero is unset. */
export function storedInPersonFeesOutOfRange(
  storedFees: BusinessMerchantServiceChargeFee[] | undefined,
  profiles: IPurchPriceProfile[] | undefined,
  storedProfileId: string | null | undefined
): boolean {
  const fallback = profiles?.find((profile) => profile.name === "default");
  const profile =
    (storedProfileId &&
      profiles?.find((candidate) => candidate._id === storedProfileId)) ||
    fallback;
  const priced = (storedFees ?? []).filter(
    (fee) => typeof fee.value === "number" && fee.value > 0
  );
  return inPersonHasOutOfRange(buildRangeGroups(priced, profile));
}

// Online out-of-range uses the same feeStatus semantics as in-person, so a 0
// below the partner floor is flagged consistently (pill + gate agree).
export function onlineHasOutOfRange(
  buckets: { value: number; valueType: ValueType; min?: number; max?: number }[]
): boolean {
  return buckets.some((b) => {
    if (typeof b.min !== "number" || typeof b.max !== "number") return false;
    if (b.min === 0 && b.max === 0) return false;
    const s = feeStatus(b.value, b.valueType, b.min, b.max);
    return s?.kind === "above" || s?.kind === "below";
  });
}

/** A real merchant rate. 0 is "not set" — the backend persists it as a rate. */
export const isPricedOnlineValue = (value: unknown): value is number =>
  typeof value === "number" && value > 0;

// The pricing endpoint requires a complete onlinePricing and blindly $sets it,
// so saving a merchant that has none would zero it. True only when at least one
// bucket carries a priced value — 0 is "not set", same as isPricedOnlineValue.
export function hasConfiguredOnlinePricing(
  onlinePricing: BusinessOnlinePricing | undefined,
  keys: OnlineGroupKey[]
): boolean {
  return keys.some((k) => isPricedOnlineValue(onlinePricing?.[k]?.value));
}

export function hasCompleteOnlinePricing(
  onlinePricing: BusinessOnlinePricing | undefined,
  keys: OnlineGroupKey[]
): boolean {
  return (
    keys.length > 0 &&
    keys.every((k) => isPricedOnlineValue(onlinePricing?.[k]?.value))
  );
}

// The save payload must carry exactly the buckets shown/editable in the UI
// (the current template's keys, unioned with any legacy key already present) —
// never the full 6-key seeded object. The endpoint blindly $sets onlinePricing,
// so an unfiltered save both creates a template-inappropriate bucket (e.g. a
// zeroed bancontact on an NL merchant) and, since it's exhaustive, would drop
// any legacy bucket not in that unfiltered set entirely.
export function filterOnlinePricingToKeys(
  onlinePricing: OnlinePartnerBucketGroups,
  keys: (keyof OnlinePartnerBucketGroups)[]
): OnlinePartnerBucketGroups {
  const keySet = new Set(keys);
  return Object.fromEntries(
    Object.entries(onlinePricing).filter(([key]) =>
      keySet.has(key as keyof OnlinePartnerBucketGroups)
    )
  ) as OnlinePartnerBucketGroups;
}

// Seed editable online state from what is stored (0 when unset). Missing
// four-bucket card keys fall back to the 16-bucket groups that hold the same
// rate; the wallet prefers Corporate, then E.E.A (consumer cards), then the
// commercial group.
export function seedOnline(
  src: BusinessOnlinePricing | undefined
): OnlinePartnerBucketGroups {
  const mk = (
    key: keyof OnlinePartnerBucketGroups,
    valueType: ValueType,
    ...fallbackKeys: (keyof OnlinePartnerBucketGroups)[]
  ) => {
    const b =
      src?.[key] ??
      fallbackKeys.map((k) => src?.[k]).find((group) => group != null);
    return {
      value: b?.value ?? 0,
      valueType: (b?.valueType as ValueType) ?? valueType,
      min: b?.min,
      max: b?.max,
    };
  };
  return {
    iDeal: mk("iDeal", "Fixed"),
    bancontact: mk("bancontact", "Fixed"),
    amex: mk("amex", "Percentage"),
    corporateCreditCards: mk(
      "corporateCreditCards",
      "Percentage",
      "internationalAndCommercial"
    ),
    nonCorporateCreditCards: mk(
      "nonCorporateCreditCards",
      "Percentage",
      "european",
      "domesticDebitCards"
    ),
    appleGooglePay: mk(
      "appleGooglePay",
      "Percentage",
      "corporateCreditCards",
      "european",
      "internationalAndCommercial"
    ),
  };
}

export function onlinePricingForProfile(
  profile: IOnlinePurchPriceProfile | undefined,
  partnerDefault: OnlinePartnerBucketGroups | undefined
): OnlinePartnerBucketGroups {
  return seedOnline({
    ...partnerDefault,
    ...profile?.defaultOnlinePricing,
  } as OnlinePartnerBucketGroups);
}

const sameOnlineRate = (
  a: OnlinePartnerBucketGroups[OnlineGroupKey],
  b: OnlinePartnerBucketGroups[OnlineGroupKey]
): boolean => !!a && !!b && a.value === b.value && a.valueType === b.valueType;

// A range switch keeps every rate the merchant carries and fills only the
// unpriced buckets from the new range — the backend's mapping seed does the
// same. A bucket the previous range filled still holding that range's rate is
// not the merchant's own, so the next switch fills it again rather than
// carrying one range's default under another. The band check then flags
// whatever the new range does not allow.
export function fillUnpricedOnlinePricing(
  current: OnlinePartnerBucketGroups,
  defaults: OnlinePartnerBucketGroups,
  previousSeed: OnlinePartnerBucketGroups = {}
): { pricing: OnlinePartnerBucketGroups; seeded: OnlinePartnerBucketGroups } {
  const keys = new Set([
    ...Object.keys(current),
    ...Object.keys(defaults),
  ]) as Set<OnlineGroupKey>;
  const pricing: OnlinePartnerBucketGroups = {};
  const seeded: OnlinePartnerBucketGroups = {};
  keys.forEach((key) => {
    const own =
      isPricedOnlineValue(current[key]?.value) &&
      !sameOnlineRate(current[key], previousSeed[key]);
    if (own || !defaults[key]) {
      pricing[key] = current[key];
      return;
    }
    pricing[key] = defaults[key];
    seeded[key] = defaults[key];
  });
  return { pricing, seeded };
}

export type MappingOnlineGroupRate = { value: number; valueType: ValueType };

function pricedMappingRates(
  source: OnlinePartnerBucketGroups | undefined
): Partial<Record<OnlineGroupKey, MappingOnlineGroupRate>> {
  const rates: Partial<Record<OnlineGroupKey, MappingOnlineGroupRate>> = {};
  (Object.keys(source ?? {}) as OnlineGroupKey[]).forEach((key) => {
    const group = source?.[key];
    if (!group || !isPricedOnlineValue(group.value)) return;
    rates[key] = {
      value: group.value,
      valueType: (group.valueType as ValueType) ?? "Percentage",
    };
  });
  return rates;
}

/** Merchant defaults the selected range will apply. Profile wins over partner. */
export function mappingOnlineGroupRates(params: {
  profile?: Pick<IOnlinePurchPriceProfile, "defaultOnlinePricing">;
  partnerDefault?: OnlinePartnerBucketGroups;
}): Partial<Record<OnlineGroupKey, MappingOnlineGroupRate>> {
  // Filter each source first: a profile 0 must not clobber a priced partner root.
  return {
    ...pricedMappingRates(params.partnerDefault),
    ...pricedMappingRates(
      params.profile?.defaultOnlinePricing as
        OnlinePartnerBucketGroups | undefined
    ),
  };
}

// ---------------------------------------------------------------------------
// PC-1316: in-person and online pricing save on their own buttons. Both go to
// PUT /businesses/{id}/pricing and both carry the partner mapping, so either
// side can be the one that maps the merchant.
// ---------------------------------------------------------------------------

export type PricingSaveMapping = {
  partnerId: string | null;
  partnerPurchPriceProfileId: string | null;
  partnerOnlinePurchPriceProfileId: string | null;
};

/** The in-person save: the edited fees and the mapping. Online fields are left
 *  out entirely — the endpoint reads an absent `onlinePricing` as "leave online
 *  untouched", while `{}` is refused as incomplete. The online range is left
 *  out too once a partner is stored, which keeps the stored one; only a first
 *  map, or a range the partner no longer links (`null`), writes the one picked.
 *
 *  An empty fee list is left out the same way. The endpoint replaces the stored
 *  list wholesale, so `[]` from a card that loaded no buckets would erase every
 *  stored fee (PC-1394); the mapping still saves. */
export function buildInPersonPricingSave(params: {
  fees: BusinessMerchantServiceChargeFee[];
  storedPartnerId?: string;
  mapping: PricingSaveMapping;
}): UpdateBusinessPricingPayload {
  const { partnerOnlinePurchPriceProfileId, ...mapping } = params.mapping;
  return {
    ...(params.fees.length ? { merchantServiceChargeFees: params.fees } : {}),
    ...mapping,
    ...(params.storedPartnerId && partnerOnlinePurchPriceProfileId !== null
      ? {}
      : { partnerOnlinePurchPriceProfileId }),
  };
}

/** The online save. In-person fees and the in-person range are left out, which
 *  the endpoint reads as "leave them untouched": in-person stays as stored, even
 *  on a first map, until the in-person save confirms the range it picked. */
export function buildOnlinePricingSave(params: {
  mapping: PricingSaveMapping;
  onlinePricing: BusinessOnlinePricing;
  onlineMerchantServiceChargeFees?: BusinessMerchantServiceChargeFee[];
}): UpdateBusinessPricingPayload {
  const { mapping } = params;
  return {
    partnerId: mapping.partnerId,
    partnerOnlinePurchPriceProfileId: mapping.partnerOnlinePurchPriceProfileId,
    onlinePricing: params.onlinePricing,
    ...(params.onlineMerchantServiceChargeFees?.length
      ? {
          onlineMerchantServiceChargeFees:
            params.onlineMerchantServiceChargeFees,
        }
      : {}),
  };
}

export type PricingSaveSection = "inPerson" | "online" | "mapping";

const SAVE_ERROR_SECTION: Record<string, PricingSaveSection> = {
  IN_PERSON_PRICING_OUT_OF_RANGE: "inPerson",
  IN_PERSON_PRICING_VALUETYPE_MISMATCH: "inPerson",
  PRICING_OUT_OF_RANGE: "online",
  PRICING_VALUETYPE_MISMATCH: "online",
  ONLINE_PRICING_STRUCTURE_DOWNGRADE: "online",
  ADYEN_ONLINE_PRICING_SYNC_FAILED: "online",
  PartnerReassignmentNotAllowed: "mapping",
};

/** Which part of the pane a refused save belongs to. A code this table does not
 *  name stays with the section whose button sent it. */
export function pricingSaveErrorSection(
  code: string | undefined,
  sentBy: Exclude<PricingSaveSection, "mapping">
): PricingSaveSection {
  return (code && SAVE_ERROR_SECTION[code]) || sentBy;
}
